import {
  Info as InfoIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  SearchOff as SearchOffIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Checkbox,
  Collapse,
  IconButton,
  TableBody as MuiTableBody,
  Skeleton,
  Stack,
  SxProps,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  lighten,
} from "@mui/material";
import { ChangeEvent, Fragment, MouseEvent, useState } from "react";

import { TableColumnAction, TableOptions, TableProps } from "../..";
import { formatRecord, getDefaultOptions } from "../../helpers";

import { theme } from "@/theme";
interface TableBodyProps<TRecord>
  extends Pick<
    TableProps<TRecord>,
    "data" | "columns" | "renderDetailPanel" | "loading" | "getMissingValues" | "getTableRecordId" | "onRowClick"
  > {
  selected?: TRecord[];
  onSelect?: (checked: boolean, record: TRecord) => void;
  expanded?: TRecord[];
  onExpand?: (record: TRecord) => void;
  columnActions?: TableColumnAction<TRecord>[];
  rowsPerPage?: number;
  isEmpty?: boolean;
  emptyRows?: number;
  options?: Partial<
    Pick<
      TableOptions<TRecord>,
      | "bodyStyle"
      | "selectable"
      | "size"
      | "getColumnStyles"
      | "getRowStyles"
      | "getCellStyles"
      | "emptyRowStyle"
      | "rowClickable"
    >
  >;
}

export function TableBody<TRecord>({
  data,
  isEmpty = false,
  columns,
  columnActions = [],
  selected = [],
  expanded = [],
  onExpand,
  loading,
  rowsPerPage = 0,
  emptyRows = 0,
  renderDetailPanel,
  getMissingValues,
  getTableRecordId,
  onRowClick,
  onSelect,
  options,
}: TableBodyProps<TRecord>) {
  const [hoveredRowIndex, setHoveredRowIndex] = useState(-1);
  const defaultOptions = getDefaultOptions<TRecord>(columns, options);
  const {
    selectable = defaultOptions.selectable,
    bodyStyle = defaultOptions.bodyStyle,
    size = defaultOptions.size,
    getColumnStyles = defaultOptions.getColumnStyles,
    getRowStyles = defaultOptions.getRowStyles,
    getCellStyles = defaultOptions.getCellStyles,
    emptyRowStyle = defaultOptions.emptyRowStyle,
    rowClickable = defaultOptions.rowClickable,
  } = options ?? defaultOptions;
  const hasRowAction = rowClickable && !!onRowClick;

  const checkAndPreventPropagation = (
    event: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement | SVGSVGElement>
  ) => {
    // If row click is enabled, stop propagation so it doesn't trigger the onRowClick as well
    if (hasRowAction) {
      event.stopPropagation();
    }
  };

  return (
    <MuiTableBody sx={bodyStyle}>
      {loading ? (
        [...Array(rowsPerPage)].map((_, i) => {
          return (
            <TableRow key={i}>
              {selectable && (
                <TableCell padding="checkbox">
                  <Skeleton />
                </TableCell>
              )}
              {renderDetailPanel && (
                <TableCell>
                  <Skeleton />
                </TableCell>
              )}
              {columns.map((column) => {
                const { field, cellStyle: columnCellStyle = {} } = column;

                return (
                  <TableCell
                    key={field || column.title}
                    sx={{ ...getColumnStyles(column), ...columnCellStyle } as SxProps<typeof theme>}
                  >
                    <Skeleton />
                  </TableCell>
                );
              })}
              {columnActions.length > 0 && (
                <TableCell>
                  <Skeleton />
                </TableCell>
              )}
            </TableRow>
          );
        })
      ) : (
        <>
          {isEmpty ? (
            <TableRow>
              <TableCell
                sx={{ py: 3.5 }}
                colSpan={
                  columns.length + (selectable ? 1 : 0) + (columnActions.length ? 1 : 0) + (renderDetailPanel ? 1 : 0)
                }
                align="center"
              >
                <Avatar sx={{ margin: "0 auto" }}>
                  <SearchOffIcon />
                </Avatar>
                <Typography sx={{ mt: 1.5 }} variant="body2" color="textSecondary">
                  Nothing to show
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((record, rowIndex) => {
              const missingValues = getMissingValues ? getMissingValues(record) : [];

              return (
                <Fragment key={getTableRecordId(record) || rowIndex}>
                  <TableRow
                    sx={{
                      cursor: hasRowAction ? "pointer" : "initial",
                      ...(renderDetailPanel && { "& > td": { borderBottom: "unset" } }),
                      ...(missingValues.length && { bgcolor: (theme) => lighten(theme.palette.error.main, 0.9) }),
                      ...(hasRowAction && {
                        "&:hover": {
                          borderRadius: 1,
                          boxShadow: `0 0px 2px 1px ${theme.palette.grey[400]} inset`,
                          WebkitBoxShadow: `0 0px 2px 1px ${theme.palette.grey[400]} inset`,
                        },
                      }),
                      ...getRowStyles(record),
                    }}
                    onMouseEnter={() => {
                      setHoveredRowIndex(rowIndex);
                    }}
                    onMouseLeave={() => setHoveredRowIndex(-1)}
                    onClick={(event) => {
                      // If the rows are selectable, clicking on the checkbox will trigger this onClick first before triggering the onChange on the checkbox

                      const isCheckboxClick = (event.target as any).type === "checkbox";

                      if (hasRowAction && !isCheckboxClick) {
                        onRowClick(record);
                      }
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox" width={1}>
                        <Checkbox
                          color="primary"
                          size={size}
                          checked={selected.some((item) => getTableRecordId(item) === getTableRecordId(record))}
                          onChange={(event) => {
                            checkAndPreventPropagation(event);
                            if (onSelect) {
                              onSelect(event.target.checked, record);
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    {renderDetailPanel && (
                      <TableCell width={1}>
                        <IconButton
                          size="small"
                          sx={{ visibility: renderDetailPanel(record) === null ? "hidden" : "initial" }}
                          onClick={(event) => {
                            checkAndPreventPropagation(event);
                            if (onExpand) {
                              onExpand(record);
                            }
                          }}
                        >
                          {expanded.some((item) => getTableRecordId(item) === getTableRecordId(record)) ? (
                            <KeyboardArrowDownIcon />
                          ) : (
                            <KeyboardArrowRightIcon />
                          )}
                        </IconButton>
                      </TableCell>
                    )}
                    {columns.map((column, index) => {
                      const { field, format, formatOptions, render, cellStyle: columnCellStyle = {} } = column;
                      return (
                        <TableCell
                          key={`${field}-${index}`}
                          sx={
                            {
                              ...getColumnStyles(column),
                              ...getCellStyles(record, column),
                              ...columnCellStyle,
                              ...(format === "currency" && { textAlign: "right" }),
                            } as SxProps<typeof theme>
                          }
                        >
                          {render ? render(record) : formatRecord(record, field, format, formatOptions)}
                        </TableCell>
                      );
                    })}
                    {(columnActions.length > 0 || missingValues.length > 0) && (
                      <TableCell width={1}>
                        <Stack direction="row" alignItems="center">
                          {columnActions.map(
                            (
                              { icon, color = "default", tooltip, getIcon, getColor, getTooltip, getDisabled, onClick },
                              index
                            ) => {
                              return (
                                <Tooltip
                                  key={`${getTableRecordId(record)}-${index}`}
                                  title={getTooltip ? getTooltip(record) : tooltip || ""}
                                >
                                  <Box>
                                    <IconButton
                                      sx={{
                                        "& svg": { fontSize: 22 },
                                        visibility: rowIndex === hoveredRowIndex ? "visible" : "hidden",
                                      }}
                                      onClick={(event) => {
                                        checkAndPreventPropagation(event);
                                        onClick(record, rowIndex, event);
                                      }}
                                      color={getColor ? getColor(record) : color}
                                      disabled={getDisabled && getDisabled(record)}
                                    >
                                      {getIcon ? getIcon(record) : icon}
                                    </IconButton>
                                  </Box>
                                </Tooltip>
                              );
                            }
                          )}
                          {missingValues.length > 0 && (
                            <Tooltip
                              title={
                                <>
                                  <Typography>Missing Values:</Typography>
                                  {missingValues.map((text) => (
                                    <Typography key={text}>{text}</Typography>
                                  ))}
                                </>
                              }
                            >
                              <InfoIcon
                                className="missing-values-icon"
                                sx={{ mx: 1, color: "error.main", fontSize: 20 }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                  {renderDetailPanel && (
                    <TableRow>
                      <TableCell
                        sx={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={(selectable ? 1 : 0) + 1 + (columnActions.length ? 1 : 0) + columns.length}
                      >
                        <Collapse
                          in={expanded.some((item) => getTableRecordId(item) === getTableRecordId(record))}
                          timeout="auto"
                          unmountOnExit
                        >
                          {renderDetailPanel(record)}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })
          )}
          {emptyRows > 0 && (
            <TableRow sx={{ height: size === "small" ? 35 : 55 * emptyRows, ...emptyRowStyle }}>
              <TableCell
                colSpan={
                  (selectable ? 1 : 0) + (renderDetailPanel ? 1 : 0) + (columnActions.length ? 1 : 0) + columns.length
                }
              />
            </TableRow>
          )}
        </>
      )}
    </MuiTableBody>
  );
}
